import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiEye,
  FiHeart,
  FiClock,
  FiCalendar,
  FiUser,
  FiTag,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import articleService from '../../services/articleService';
import { Article } from '../../types/article.types';
import Button from '../../components/common/Button/Button';
import Card from '../../components/common/Card/Card';
import Loader from '../../components/common/Loader/Loader';
import { formatToIST, getRelativeTime } from '../../utils/dateHelper';
import styles from './ViewArticle.module.css';

const ViewArticle: React.FC = () => {
  const { id: slug } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
    } else {
      toast.error('Invalid article URL');
      navigate('/top-news');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchArticle = async (articleSlug: string) => {
    try {
      setLoading(true);
      console.log('📖 Fetching article with slug:', articleSlug);

      const data = await articleService.getArticleBySlug(articleSlug);
      console.log('✅ Article loaded:', data.headline);

      setArticle(data);

      // Increment view count (non-blocking)
      articleService.incrementViews(articleSlug).catch((err) =>
        console.log('View count update skipped:', err)
      );
    } catch (error: any) {
      console.error('❌ Failed to fetch article:', error);
      const errorMessage =
        error.response?.data?.message || 'Article not found. It may have been deleted or the URL is incorrect.';
      toast.error(errorMessage);
      navigate('/top-news');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!article) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${article.headline}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      console.log('🗑️ Deleting article with ID:', article.id);
      await articleService.deleteArticle(article.id); // Use ID, not slug
      toast.success('Article deleted successfully');
      navigate('/top-news');
    } catch (error: any) {
      console.error('❌ Delete failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete article');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      tech: '#3B82F6',
      innovation: '#8B5CF6',
      unicorn: '#F59E0B',
      trending: '#DC2626',
      featured: '#7C3AED',
      'women-empowerment': '#DB2777',
      farming: '#65A30D',
      'case-study': '#0369A1',
      'early-birds-startup': '#10B981',
      recognition: '#EF4444',
      startuprahi: '#F97316',
      manufacturing: '#6366F1',
      'stock-market': '#14B8A6',
      marketing: '#EC4899',
      stories: '#0891B2',
    };
    return colors[category] || '#64748b';
  };

  const getCategoryLabel = (category: string) => {
    return category
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader size="large" text="Loading article..." />
      </div>
    );
  }

  if (!article) {
    return (
      <div className={styles.loaderContainer}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-lg)' }}>
            Article Not Found
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-xl)' }}>
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="primary" onClick={() => navigate('/top-news')}>
            Back to Articles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.viewArticle}>
      {/* Header Actions */}
      <div className={styles.headerActions}>
        <Button variant="ghost" icon={<FiArrowLeft />} onClick={() => navigate('/top-news')}>
          Back to Articles
        </Button>

        <div className={styles.actions}>
          <Button
            variant="primary"
            icon={<FiEdit />}
            onClick={() => navigate(`/articles/edit/${article.slug}`)}
          >
            Edit Article
          </Button>
          {(user?.role === 'superadmin' || user?.role === 'developer') && (
            <Button variant="danger" icon={<FiTrash2 />} onClick={handleDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Featured Image */}
      <div className={styles.featuredImageContainer}>
        <img
          src={article.featuredImage.url}
          alt={article.headline}
          className={styles.featuredImage}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'https://via.placeholder.com/1200x600/3B82F6/FFFFFF?text=No+Image';
          }}
        />
        {article.isFeatured && <span className={styles.featuredBadge}>⭐ Featured</span>}
        {article.isBreakingNews && <span className={styles.breakingBadge}>🔥 Breaking News</span>}
      </div>

      {/* Article Meta */}
      <Card className={styles.metaCard}>
        <div className={styles.metaGrid}>
          <div className={styles.metaItem}>
            <span
              className={styles.categoryBadge}
              style={{ backgroundColor: getCategoryColor(article.category) }}
            >
              {getCategoryLabel(article.category)}
            </span>
            <span
              className={`${styles.statusBadge} ${
                styles[`status${article.status.charAt(0).toUpperCase() + article.status.slice(1)}`]
              }`}
            >
              {article.status}
            </span>
          </div>

          <div className={styles.metaStats}>
            <div className={styles.statItem}>
              <FiEye />
              <span>{(article.views || 0).toLocaleString()} views</span>
            </div>
            <div className={styles.statItem}>
              <FiHeart />
              <span>{(article.likes || 0).toLocaleString()} likes</span>
            </div>
            <div className={styles.statItem}>
              <FiClock />
              <span>{article.readTime} min read</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Article Content */}
      <Card className={styles.contentCard}>
        <h1 className={styles.headline}>{article.headline}</h1>

        <div className={styles.authorSection}>
          <img
            src={
              article.author?.profilePhoto?.url ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(article.author?.name || 'Author')}&background=3B82F6&color=fff&size=128`
            }
            alt={article.author?.name || 'Author'}
            className={styles.authorAvatar}
          />
          <div className={styles.authorInfo}>
            <p className={styles.authorName}>
              <FiUser /> {article.author?.name || 'Unknown Author'}
            </p>
           
            <p className={styles.publishDate}>
              <FiCalendar /> Published {formatToIST(article.createdAt, 'dd MMM yyyy, hh:mm a')} IST ||<p className={styles.relativeTime}>{getRelativeTime(article.createdAt)}</p>
            </p>
            
          </div>
        </div>

        {article.excerpt && article.excerpt.trim() && (
          <div className={styles.excerpt}>
            <p>{article.excerpt}</p>
          </div>
        )}

        <div className={styles.description} dangerouslySetInnerHTML={{ __html: article.description }} />

        {article.tags && article.tags.length > 0 && (
          <div className={styles.tagsSection}>
            <FiTag className={styles.tagIcon} />
            <div className={styles.tags}>
              {article.tags.map((tag, index) => (
                <span key={index} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* SEO Info (Visible to admins) */}
      {(user?.role === 'superadmin' || user?.role === 'developer') && article.seo && (
        <Card title="SEO Information" className={styles.seoCard}>
          <div className={styles.seoInfo}>
            <div className={styles.seoItem}>
              <strong>Meta Title:</strong>
              <p>{article.seo.metaTitle || 'Not set'}</p>
            </div>
            <div className={styles.seoItem}>
              <strong>Meta Description:</strong>
              <p>{article.seo.metaDescription || 'Not set'}</p>
            </div>
            <div className={styles.seoItem}>
              <strong>Slug:</strong>
              <p>{article.slug}</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ViewArticle;